import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { ArrowRight, MessageSquare, Paperclip, X } from 'lucide-react';
import { CHAT_MESSAGE_MAX, type ChatMessageView, type ChatView } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Skeleton } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import {
  AppointmentCard,
  AttachmentRequestCard,
  SlotsCard,
  TicketCardView,
  TransmittedCard,
} from './ChatCards';
import type { ChatController } from './useChat';
import styles from './Questions.module.css';

function Bubble({ role, children }: { role: ChatMessageView['role']; children: ReactNode }) {
  return (
    <div className={clsx(styles.bubble, role === 'customer' ? styles.customer : styles.bot)}>{children}</div>
  );
}

/** Index of the last message carrying an appointment card (slots before it are history). */
function lastAppointmentIndex(messages: ChatMessageView[]): number {
  let index = -1;
  messages.forEach((message, position) => {
    if (message.card?.kind === 'appointment') index = position;
  });
  return index;
}

function lastIndexOfKind(messages: ChatMessageView[], kind: 'slots' | 'attachment_request'): number {
  let index = -1;
  messages.forEach((message, position) => {
    if (message.card?.kind === kind) index = position;
  });
  return index;
}

/**
 * One chat entry: the bubble and its card in the order of the frames — slots and the receipt row follow their
 * bubble (23/25), the appointment and the ticket card precede it (24/26), „Das wurde übermittelt" stands alone.
 */
function ChatEntry({
  message,
  index,
  view,
  controller,
  onOpenTicket,
}: {
  message: ChatMessageView;
  index: number;
  view: ChatView;
  controller: ChatController;
  onOpenTicket: (ticketId: string) => void;
}) {
  const card = message.card;
  const text = message.attachment ? (
    <Bubble role={message.role}>
      <span className={styles.attachmentBubble}>
        <Paperclip size={15} strokeWidth={1.75} aria-hidden="true" />
        {message.attachment.name}
      </span>
    </Bubble>
  ) : message.text ? (
    <Bubble role={message.role}>{message.text}</Bubble>
  ) : null;
  const time = (
    <span className={styles.srOnly}>
      {message.role === 'customer' ? 'Sie' : view.header.title}, {message.atLabel}:{' '}
    </span>
  );

  if (!card) {
    return text ? (
      <>
        {time}
        {text}
      </>
    ) : null;
  }
  switch (card.kind) {
    case 'slots': {
      const active =
        index === lastIndexOfKind(view.messages, 'slots') && lastAppointmentIndex(view.messages) < index;
      return (
        <>
          {time}
          {text}
          <SlotsCard card={card} active={active} onBook={controller.book} />
        </>
      );
    }
    case 'attachment_request': {
      const active =
        view.step === 'support_urgency' && index === lastIndexOfKind(view.messages, 'attachment_request');
      return (
        <>
          {time}
          {text}
          <AttachmentRequestCard
            label={card.label}
            hint={card.hint}
            active={active}
            onPick={(file) => void controller.attach(file)}
          />
        </>
      );
    }
    case 'appointment':
      return (
        <>
          {time}
          <AppointmentCard card={card} onCancel={controller.cancel} />
          {text}
        </>
      );
    case 'transmitted':
      return (
        <>
          {time}
          <TransmittedCard title={card.title} text={card.text} />
        </>
      );
    case 'ticket':
      return (
        <>
          {time}
          <TicketCardView ticket={card.ticket} />
          {text}
          <button
            type="button"
            className={clsx('pressable', styles.openTicket)}
            onClick={() => onOpenTicket(card.ticket.id)}
          >
            Ticket im Portal öffnen
          </button>
        </>
      );
  }
}

/**
 * The chat „DAMOKLES Assistent" (Mandantenportal 22–26): header with the API's line, the messages in a
 * `role="log"`, the quick-reply chips, the input with the 38 px send button. Docked on /questions (440 px), a
 * full-screen sheet below 768 px. The close cross collapses the docked panel.
 */
export function ChatPanel({
  controller,
  onClose,
  onOpenTicket,
  className,
  titleId = 'portal-chat-title',
}: {
  controller: ChatController;
  onClose?: (() => void) | undefined;
  onOpenTicket: (ticketId: string) => void;
  className?: string | undefined;
  titleId?: string | undefined;
}) {
  const { chat, sending, error } = controller;
  const [draft, setDraft] = useState('');
  const log = useRef<HTMLDivElement>(null);
  const view = chat.data;
  const count = view?.messages.length ?? 0;

  useEffect(() => {
    const element = log.current;
    if (element) element.scrollTop = element.scrollHeight;
  }, [count, view?.quickReplies.length]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const text = draft.trim();
    if (!text || sending) return;
    if (await controller.sendText(text)) setDraft('');
  }

  return (
    <section className={clsx(styles.panel, className)} aria-labelledby={titleId}>
      <div className={styles.panelHead}>
        <span className={styles.botAvatar} aria-hidden="true">
          <MessageSquare size={17} strokeWidth={1.5} />
        </span>
        <div className={styles.panelTitleBlock}>
          <h2 className={styles.panelTitle} id={titleId}>
            {view?.header.title ?? 'DAMOKLES Assistent'}
          </h2>
          {view ? <div className={styles.panelLine}>{view.header.line}</div> : null}
        </div>
        {onClose ? (
          <button type="button" className={styles.close} onClick={onClose} aria-label="Chat schließen">
            <X size={18} strokeWidth={1.75} aria-hidden="true" />
          </button>
        ) : null}
      </div>
      <div
        className={styles.log}
        ref={log}
        role="log"
        aria-live="polite"
        aria-relevant="additions"
        tabIndex={0}
      >
        {chat.isPending ? (
          <div className={styles.logSkeleton} aria-busy="true">
            <Skeleton width="70%" height={64} />
            <Skeleton width="45%" height={36} />
          </div>
        ) : chat.isError ? (
          <div className={styles.cardError} role="alert">
            Der Chat konnte nicht geladen werden. {errorMessage(chat.error)}{' '}
            <button type="button" className={styles.cardLink} onClick={() => void chat.refetch()}>
              Erneut laden
            </button>
          </div>
        ) : view ? (
          <>
            {view.messages.map((message, index) => (
              <ChatEntry
                key={message.id}
                message={message}
                index={index}
                view={view}
                controller={controller}
                onOpenTicket={onOpenTicket}
              />
            ))}
            {view.quickReplies.length > 0 ? (
              <div className={styles.chips}>
                {view.quickReplies.map((reply) => (
                  <button
                    key={reply.key}
                    type="button"
                    className={styles.chip}
                    disabled={sending}
                    onClick={() => void controller.sendQuickReply(reply.key)}
                  >
                    {reply.label}
                  </button>
                ))}
              </div>
            ) : null}
          </>
        ) : null}
      </div>
      {error ? (
        <div className={styles.sendError} role="alert">
          {error}
        </div>
      ) : null}
      <form className={styles.inputRow} onSubmit={(event) => void submit(event)}>
        <input
          className={styles.input}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={view?.placeholder ?? 'Ihre Frage eingeben'}
          aria-label="Nachricht an den DAMOKLES Assistenten"
          maxLength={CHAT_MESSAGE_MAX}
          disabled={!view}
        />
        <button
          type="submit"
          className={clsx('pressable', styles.send)}
          disabled={sending || !view}
          aria-label="Senden"
        >
          <ArrowRight size={17} strokeWidth={1.75} aria-hidden="true" />
        </button>
      </form>
    </section>
  );
}
